import * as yup from 'yup';

import locales from 'src/i18n/locales/en.json';
import type { DateOfBirthType } from 'src/components/date-of-birth-input/types';
import { PhoneNumber } from 'src/components/phone-input/types';
import { validateDateOfBirth } from 'src/utils/validators/validate-date-of-birth';
import { validateEmail } from 'src/utils/validators/validate-email';
import { validatePassword } from 'src/utils/validators/validate-password';
import { validatePhoneNumber } from 'src/utils/validators/validate-phone-number';
import { validateTaxId } from 'src/api/userService';

import {
    countries,
    defaultOption,
    userDataKeys,
    nieNifMaxLength,
    nationalities,
    languages,
    universitiesCountryMap,
    studyYears,
} from './constants';

const universities = [
    defaultOption.value,
    ...universitiesCountryMap.en,
    ...universitiesCountryMap.es,
];

const errorMessages = {
    firstName: {
        required: 'your_details_form_first_name_required',
        invalid: 'your_details_form_first_name_invalid',
    },
    lastName: {
        required: 'your_details_form_last_name_required',
        invalid: 'your_details_form_last_name_invalid',
    },
    preferredName: {
        invalid: 'your_details_form_pefered_name_invalid',
    },
    email: {
        required: 'your_details_form_email_required',
        max: 'your_details_form_email_max',
        invalid: 'your_details_form_email_invalid',
    },
    password: {
        required: 'your_details_form_password_required',
        min: 'your_details_form_password_min',
        max: 'your_details_form_password_max',
        invalid: 'your_details_form_password_invalid',
    },
    repeatPassword: {
        required: 'your_details_form_repeat_password_required',
        invalid: 'your_details_form_repeat_password_invalid',
    },
    phone: {
        required: 'your_details_form_phone_required',
        invalid: 'your_details_form_phone_invalid',
    },
    dateOfBirth: {
        required: 'your_details_form_date_of_birth_required',
        invalid: 'your_details_form_date_of_birth_invalid',
    },
    address1: {
        required: 'your_details_form_address1_required',
        invalid: 'your_details_form_address1_invalid',
    },
    address2: {
        required: 'your_details_form_address2_required',
        invalid: 'your_details_form_address2_invalid',
    },
    city: {
        required: 'your_details_form_city_required',
        invalid: 'your_details_form_city_invalid',
    },
    postCode: {
        required: 'your_details_form_post_code_required',
        invalidFormat: 'your_details_form_post_code_invalidFormat',
        invalid: 'your_details_form_post_code_invalid',
    },
    country: {
        required: 'your_details_form_country_required',
    },
    nationality: {
        required: 'your_details_form_nationality_required',
    },
    preferredLanguage: {
        required: 'your_details_form_language_required',
    },
    university: {
        required: 'your_details_form_university_required',
    },
    studyYear: {
        required: 'your_details_form_study_years_required',
    },
    isSpanishResident: {
        required: 'your_details_form_is_spanish_resident_required',
    },
    nifNumber: {
        required: 'your_details_form_nif_required',
        invalid: 'your_details_form_nif_invalid',
    },
    nieNumber: {
        required: 'your_details_form_nie_required',
        invalid: 'your_details_form_nie_invalid',
    },
    passportNumber: {
        required: 'your_details_form_passport_number_required',
        invalid: 'your_details_form_passport_number_invalid',
    },
    passportIssuingCountry: {
        required: 'your_details_form_passport_country_required',
    },
    userFormConfirmation: {
        required: 'your_details_form_confirmation_required',
    },
};

export const createUserDetailsSchema = (
    validatePasswordFields: boolean,
    isSpanishBooking: boolean
) =>
    yup.object().shape({
        [userDataKeys.firstName]: yup
            .string()
            .required(errorMessages.firstName.required)
            .max(40, errorMessages.firstName.invalid),
        [userDataKeys.lastName]: yup
            .string()
            .required(errorMessages.lastName.required)
            .max(80, errorMessages.lastName.invalid),
        [userDataKeys.preferredName]: yup
            .string()
            .nullable()
            .max(40, errorMessages.preferredName.invalid),
        [userDataKeys.email]: yup
            .string()
            .required(errorMessages.email.required)
            .max(80, errorMessages.email.max)
            .test({ message: errorMessages.email.invalid, test: validateEmail }),
        [userDataKeys.password]: yup
            .string()
            .when(() =>
                validatePasswordFields
                    ? yup
                          .string()
                          .required(errorMessages.password.required)
                          .min(8, errorMessages.password.min)
                          .max(80, errorMessages.password.max)
                          .test({ message: errorMessages.password.invalid, test: validatePassword })
                    : yup.string().notRequired()
            ),
        [userDataKeys.repeatPassword]: yup.string().when(() =>
            validatePasswordFields
                ? yup
                      .string()
                      .required(errorMessages.repeatPassword.required)
                      .oneOf([yup.ref(userDataKeys.password)], errorMessages.repeatPassword.invalid)
                : yup.string().notRequired()
        ),
        [userDataKeys.phone]: yup
            .mixed<PhoneNumber>()
            .required(errorMessages.phone.required)
            .nonNullable(errorMessages.phone.required)
            .test({ message: errorMessages.phone.invalid, test: validatePhoneNumber }),
        [userDataKeys.dateOfBirth]: yup
            .mixed<DateOfBirthType>()
            .required(errorMessages.dateOfBirth.required)
            .test({ message: errorMessages.dateOfBirth.invalid, test: validateDateOfBirth }),
        [userDataKeys.address1]: yup
            .string()
            .max(80, errorMessages.address1.invalid)
            .required(errorMessages.address1.required),
        [userDataKeys.address2]: yup
            .string()
            .max(80, errorMessages.address2.invalid)
            .required(errorMessages.address2.required),
        [userDataKeys.city]: yup
            .string()
            .required(errorMessages.city.required)
            .max(40, errorMessages.city.invalid),
        [userDataKeys.postCode]: yup
            .string()
            .required(errorMessages.postCode.required)
            .matches(/^[A-Za-z0-9-\s]+$/, errorMessages.postCode.invalidFormat)
            .max(10, errorMessages.postCode.invalid),
        [userDataKeys.country]: yup
            .string()
            .required(errorMessages.country.required)
            .notOneOf([defaultOption.value], errorMessages.country.required)
            .oneOf(
                countries.map((c) => c.value),
                errorMessages.country.required
            ),
        [userDataKeys.nationality]: yup
            .string()
            .required(errorMessages.nationality.required)
            .notOneOf([defaultOption.value], errorMessages.nationality.required)
            .oneOf(
                nationalities.map((n) => n.value),
                errorMessages.nationality.required
            ),
        [userDataKeys.preferredLanguage]: yup
            .string()
            .required(errorMessages.preferredLanguage.required)
            .oneOf(
                languages.map((l) => l.value).filter((v) => v !== defaultOption.value),
                errorMessages.preferredLanguage.required
            ),
        [userDataKeys.university]: yup
            .string()
            .required(errorMessages.university.required)
            .oneOf(
                universities.filter((u) => u !== defaultOption.value),
                errorMessages.university.required
            ),
        [userDataKeys.studyYear]: yup
            .string()
            .required(errorMessages.studyYear.required)
            .oneOf(
                studyYears.map((s) => s.value).filter((v) => v !== defaultOption.value),
                errorMessages.studyYear.required
            ),
        [userDataKeys.isSpanishResident]: yup
            .string()
            .when([userDataKeys.nationality], ([nationality]) =>
                isSpanishBooking && nationality !== locales.nationalities_spanish
                    ? yup
                          .string()
                          .required(errorMessages.isSpanishResident.required)
                          .notOneOf([defaultOption.value], errorMessages.isSpanishResident.required)
                    : yup.string().nullable()
            ),
        [userDataKeys.nifNumber]: yup.string().when([userDataKeys.nationality], ([nationality]) =>
            isSpanishBooking && nationality === locales.nationalities_spanish
                ? yup
                      .string()
                      .transform((value) => value && value.replace(/\s+/g, ''))
                      .max(nieNifMaxLength, errorMessages.nifNumber.invalid)
                      .required(errorMessages.nifNumber.required)
                      .test('isValidNIF', errorMessages.nifNumber.invalid, async (value) => {
                          if (!value) return false;
                          const response = await validateTaxId(value);
                          return response.isValid;
                      })
                : yup.string().nullable()
        ),
        [userDataKeys.nieNumber]: yup
            .string()
            .when(
                [userDataKeys.nationality, userDataKeys.isSpanishResident],
                ([nationality, isSpanishResident]) =>
                    isSpanishBooking &&
                    nationality !== locales.nationalities_spanish &&
                    isSpanishResident === 'Yes'
                        ? yup
                              .string()
                              .transform((value) => value && value.replace(/\s+/g, ''))
                              .max(nieNifMaxLength, errorMessages.nieNumber.invalid)
                              .required(errorMessages.nieNumber.required)
                              .test(
                                  'isValidNIE',
                                  errorMessages.nieNumber.invalid,
                                  async (value) => {
                                      if (!value) return false;
                                      const response = await validateTaxId(value);
                                      return response.isValid;
                                  }
                              )
                        : yup.string().nullable()
            ),
        [userDataKeys.passportNumber]: yup
            .string()
            .when(
                [userDataKeys.nationality, userDataKeys.isSpanishResident],
                ([nationality, isSpanishResident]) =>
                    isSpanishBooking &&
                    nationality !== locales.nationalities_spanish &&
                    isSpanishResident === 'No'
                        ? yup
                              .string()
                              .max(10, errorMessages.passportNumber.invalid)
                              .required(errorMessages.passportNumber.required)
                        : yup.string().nullable()
            ),

        [userDataKeys.passportIssuingCountry]: yup
            .string()
            .when(
                [userDataKeys.nationality, userDataKeys.isSpanishResident],
                ([nationality, isSpanishResident]) =>
                    isSpanishBooking &&
                    nationality !== locales.nationalities_spanish &&
                    isSpanishResident === 'No'
                        ? yup
                              .string()
                              .required(errorMessages.passportIssuingCountry.required)
                              .oneOf(
                                  countries.map((country) => country.value),
                                  errorMessages.passportIssuingCountry.required
                              )
                        : yup.string().nullable()
            ),
        [userDataKeys.userFormConfirmation]: yup
            .boolean()
            .required(errorMessages.userFormConfirmation.required)
            .oneOf([true], errorMessages.userFormConfirmation.required),
    });
